/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable react/no-unused-prop-types */
import cx from 'classnames';
import React, { ReactElement, useRef } from 'react';
import { getOnEnter } from '../../_utils';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import styles from './base.module.scss';

export enum SwatchShapeOptions {
  DEFAULT = 'default',
  ROUND = 'round',
  ROUND_LARGE = 'round-large',
}

export interface BaseSelectorProps {
  /**
   * Unique id to identify the select.
   * @default ''
   */
  id?: string;

  /**
   * title description text for selector
   * @default ''
   */
  title?: string;

  /**
   * intial flag value to disable component
   * @default false
   */
  disabled?: boolean;

  /** prop to be passed to input control to enabled greybackground
   * @default false
   */
  hasGrayBackground?: boolean;

  /** class to have customize style
   * @default ''
   */
  className?: string;

  /** call back event if the option is selected
   * @default noop
   */
  onChange?: (event: React.MouseEvent<HTMLDivElement>, data: any) => void;

  /** render be shown as selected value */
  children: ReactElement;

  /** to set up a toggle state on the gradient select header */
  isToggle?: boolean;

  /**
   * Swatch shape for Colorpicker header
   * `round` for using legends. Only applicable for Colorpicker Selector.
   * @default 'default'
   */
  swatchShape?: SwatchShapeOptions;

  /** if gradient-picker is enabled or not
   * @default 'false'
   */
  isGradientEnabled?: boolean;

  /**
   * Can be use to set focus on the base div
   */
  baseDivRef?: React.RefObject<HTMLDivElement>;

  /** a11y prop */
  tabIndex?: number;

  /**
   * prop for accessibility
   * @default ''
   */
  ariaLabel?: string;

  /**
   * Customize the select header class
   * @default ''
   */
  customSelectHeaderClass?: string;

  /**
   * Customize the base selector class
   * @default ''
   */
  baseSelectorClass?: string;
}

export const BaseSelector = ({
  title,
  disabled,
  onChange,
  hasGrayBackground,
  children,
  isToggle = false,
  isGradientEnabled = false,
  swatchShape = SwatchShapeOptions.DEFAULT,
  className,
  baseDivRef,
  tabIndex = 0,
  ariaLabel = '',
  customSelectHeaderClass = '',
  baseSelectorClass = '',
}: BaseSelectorProps) => {
  const dropDownHeaderRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const refForBaseDiv = baseDivRef || dropDownHeaderRef;
  const onHeaderClick = (
    event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLElement>
  ) => {
    onChange?.(refForBaseDiv as any, !isToggle);
    event.stopPropagation();
  };
  const selectContainerClass = cx(styles.select, styles.selectOpen, className, {
    [styles.roundedHead]: className?.includes('roundedHead'),
  });

  const selectHeaderClass = cx({
    [styles.selectHead]: swatchShape === 'default' || swatchShape === 'round-large',
    [styles.swatchRound]: swatchShape === SwatchShapeOptions.ROUND,
    [styles.selectOpen]: isToggle,
    [styles.disabled]: disabled,
    [styles.hasGrayBackground]: hasGrayBackground,
    [customSelectHeaderClass]: customSelectHeaderClass,
  });

  const selectCaretClass = cx(
    styles.selectCaret,
    { [styles.disabled]: disabled },
    { [styles.selectOpen]: isToggle }
  );

  const renderIcon = (iconName: IconID) => (
    <Icon color={IconColor.GRAY} size={IconSize.SMALL} id={iconName} />
  );

  return (
    <div
      ref={refForBaseDiv}
      className={selectContainerClass}
      onClick={onHeaderClick}
      role="button"
      tabIndex={tabIndex}
      onKeyDown={getOnEnter(onHeaderClick)}
      data-testid="base-selector-container"
      aria-label={ariaLabel}
    >
      {title && <div className={isGradientEnabled ? '' : styles.title}>{title}</div>}
      {swatchShape === SwatchShapeOptions.DEFAULT ||
      swatchShape === SwatchShapeOptions.ROUND_LARGE ? (
        <div className={`${selectHeaderClass} ${styles.colorPicker} ${baseSelectorClass}`} data-testid="ye-hai">
          {children}
          <div className={selectCaretClass}>
            {isToggle ? renderIcon(IconID.CHEVRON_UP) : renderIcon(IconID.CHEVRON_DOWN)}
          </div>
        </div>
      ) : (
        <div className={selectHeaderClass}>{children}</div>
      )}
    </div>
  );
};
